import { useCallback, useMemo } from "react";

import { isProdLike } from "../../shared/constants";
import { type NetworkMetric, NetworkMetricsManager, NetworkMetricsNames } from "../network-metrics";

interface NetworkMetricData {
    metricName: NetworkMetricsNames;
    label: string;
    statusCode: number;
}

export const useNetworkMetrics = (): {
    createLatencyMetric: () => NetworkMetric;
    reportMetric: (data: NetworkMetricData) => void;
    reportLatencyMetric: (latencyMetric: NetworkMetric, data: NetworkMetricData) => void;
} => {
    const networkMetricsManager = useMemo(() => new NetworkMetricsManager(isProdLike()), []);

    const createLatencyMetric = useCallback(
        () => networkMetricsManager.create(NetworkMetricsNames.APOLLO_API_LATENCY),
        [networkMetricsManager],
    );

    const reportMetric = useCallback(
        ({ label, statusCode, metricName }: NetworkMetricData) => {
            // Only report metrics on network calls that have a status code
            if (statusCode) {
                networkMetricsManager.create(metricName).reportMetric({
                    dimensions: {
                        label,
                        statusCode: statusCode.toString(),
                    },
                });
            }
        },
        [networkMetricsManager],
    );

    const reportLatencyMetric = useCallback(
        (latencyMetric: NetworkMetric, { label, statusCode }: NetworkMetricData) => {
            // Only report metrics on network calls that have a status code
            if (statusCode) {
                latencyMetric.reportLatency({
                    dimensions: {
                        label,
                        statusCode: statusCode.toString(),
                    },
                });
            }
        },
        [],
    );

    return { reportMetric, createLatencyMetric, reportLatencyMetric };
};
